import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { z } from 'zod'

export const facilityModelViewSchema = z.enum([
  'assets',
  'equipment',
  'instruments',
  'all',
  'needs-review',
])

export type FacilityModelView = z.infer<typeof facilityModelViewSchema>

/** Sortable catalog columns; must stay assignable to FacilityModelColumnKey
 * (compile-time-checked in features/facilityModel/columns.ts). */
export const facilityModelSortSchema = z.enum([
  'name',
  'kind',
  'type',
  'subsystem',
  'lifecycle',
  'fillStatus',
  'slots',
  'template',
  'sources',
  'confidence',
  'validation',
])

export type FacilityModelSortKey = z.infer<typeof facilityModelSortSchema>

/**
 * Facility Model landing search params. The landing tab is URL-addressable so
 * catalog state survives reloads and tab restoration; Subject detail lives on
 * its own resource route rather than a selection param here.
 */
export const facilityModelSearchParams = z.object({
  [SEARCH_PARAM_SITE_ID]: z.string().optional(),
  view: facilityModelViewSchema.optional().catch(undefined),
  /** Live free-text name/alias/type filter. */
  q: z.string().optional(),
  /** Committed search chips, ANDed with `q`. */
  terms: z.array(z.string()).optional(),
  kind: z.string().optional(),
  type: z.string().optional(),
  subsystem: z.string().optional(),
  promoted: z.boolean().optional(),
  fillStatus: z.string().optional(),
  template: z.string().optional(),
  validation: z.string().optional(),
  minSources: z.number().optional(),
  minConfidence: z.number().optional(),
  sort: facilityModelSortSchema.optional().catch(undefined),
  sortDir: z.enum(['asc', 'desc']).optional().catch(undefined),
  /** 1-based server page; absent = first page. */
  page: z.number().int().min(1).optional().catch(undefined),
})

export type FacilityModelSearchParams = z.infer<
  typeof facilityModelSearchParams
>

export const facilityModelSubjectSearchParams = z.object({
  [SEARCH_PARAM_SITE_ID]: z.string().optional(),
})

export const sentinelDetailTabSchema = z.enum(['activity', 'model', 'settings'])

export type SentinelDetailTab = z.infer<typeof sentinelDetailTabSchema>

export const facilityModelSentinelSearchParams = z.object({
  [SEARCH_PARAM_SITE_ID]: z.string().optional(),
  tab: sentinelDetailTabSchema.optional().catch(undefined),
})

export const createFacilityModelSearch = (siteId: string | undefined) => ({
  [SEARCH_PARAM_SITE_ID]: siteId || undefined,
})
